import React, { useEffect, useMemo, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faGaugeHigh,
  faCircleCheck,
  faPhone,
  faIdCard,
  faHashtag,
  faHeadset,
  faCalendarDays,
  faArrowDown,
  faArrowUp,
  faRotateLeft,
  faBullhorn,
  faInbox,
} from '@fortawesome/free-solid-svg-icons';
import {
  getMyInfo,
  getUserBalance,
  fetchTemplateCounts,
  getDashboardAnalytics,
} from '../../../lib/api';
import { SALES_PHONE } from '../../../lib/siteContact';
import '../styles/dashboard.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const ACCENT = '#00a884';
const CHART_MUTED = 'rgba(100, 116, 139, 0.35)';

const STATUS_COLORS = {
  Read: '#38bdf8',
  Sent: '#a78bfa',
  Delivered: ACCENT,
  Failed: '#f87171',
  Pending: '#fbbf24',
  'Del. failed': '#fb7185',
};

const hasValue = (value) => value != null && String(value).trim() !== '';

const formatInt = (value) => {
  const n = Number(value);
  if (Number.isNaN(n) || value === '' || value == null) return '0';
  return new Intl.NumberFormat('en-IN').format(n);
};

const formatBalance = (value) => {
  const n = Number(value);
  if (Number.isNaN(n) || value == null) return '0';
  return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 5 }).format(n);
};

const formatMoney = (value) => {
  const n = Number(value);
  if (Number.isNaN(n) || value == null) return '0';
  return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(n);
};

const formatShortDate = (value) => {
  if (!value) return '';
  const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const pickNum = (row, snakeKey) => {
  if (!row) return 0;
  const camel = snakeKey.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
  const raw = row[snakeKey] ?? row[camel];
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
};

const deliveryRate = (stats) => {
  const total = pickNum(stats, 'total');
  const delivered = pickNum(stats, 'delivered');
  if (total <= 0) return '0% delivered';
  return `${((delivered / total) * 100).toFixed(1)}% delivered`;
};

const doughnutSafe = (a, b) => {
  const x = Math.max(0, a);
  const y = Math.max(0, b);
  if (x === 0 && y === 0) return [1, 0];
  return [x, y];
};

const EMPTY_STATS = {
  total: 0,
  read: 0,
  sent: 0,
  delivered: 0,
  failed: 0,
  pending: 0,
  delivery_failed: 0,
};

const normalizeAnalytics = (data) => {
  if (!data) return null;
  return {
    periods: {
      today: { ...EMPTY_STATS, ...(data.periods?.today || {}) },
      yesterday: { ...EMPTY_STATS, ...(data.periods?.yesterday || {}) },
      last_7_days: { ...EMPTY_STATS, ...(data.periods?.last_7_days || {}) },
      this_month: { ...EMPTY_STATS, ...(data.periods?.this_month || {}) },
    },
    daily_trend: Array.isArray(data.daily_trend) ? data.daily_trend : [],
    payments: {
      overall: { credit: 0, debit: 0, refund: 0, ...(data.payments?.overall || {}) },
      this_month: { credit: 0, debit: 0, refund: 0, ...(data.payments?.this_month || {}) },
    },
    campaigns: {
      total: data.campaigns?.total ?? 0,
      this_month: data.campaigns?.this_month ?? 0,
      recent: Array.isArray(data.campaigns?.recent) ? data.campaigns.recent : [],
    },
    whatsapp_templates: {
      total: data.whatsapp_templates?.total ?? 0,
      by_status: data.whatsapp_templates?.by_status || {},
    },
    incoming_messages: {
      today: data.incoming_messages?.today ?? 0,
      last_7_days: data.incoming_messages?.last_7_days ?? 0,
    },
  };
};

const Shimmer = ({ className = '' }) => (
  <div className={`dashboard-shimmer ${className}`.trim()} aria-hidden />
);

const AccountOverviewShimmer = () => (
  <div className="dashboard-info-grid">
    {[1, 2, 3, 4, 5].map((item) => (
      <div key={item} className="dashboard-info-item dashboard-shimmer-item">
        <Shimmer className="dashboard-shimmer-circle" />
        <div className="dashboard-shimmer-lines">
          <Shimmer className="dashboard-shimmer-line dashboard-shimmer-line--short" />
          <Shimmer className="dashboard-shimmer-line" />
        </div>
      </div>
    ))}
  </div>
);

const StatCardsShimmer = () => (
  <div className="dashboard-cards-row">
    {[1, 2, 3, 4].map((item) => (
      <div key={item} className="dashboard-shimmer-stat-card">
        <Shimmer className="dashboard-shimmer-line dashboard-shimmer-line--short" />
        <Shimmer className="dashboard-shimmer-line" style={{ marginTop: 14, height: 28, width: '45%' }} />
        <Shimmer className="dashboard-shimmer-line dashboard-shimmer-line--short" style={{ marginTop: 10 }} />
      </div>
    ))}
  </div>
);

const ChartsShimmer = () => (
  <div className="dashboard-charts-row">
    {[1, 2].map((item) => (
      <div key={item} className="dashboard-chart-card">
        <Shimmer className="dashboard-shimmer-line dashboard-shimmer-line--short" style={{ marginBottom: 16 }} />
        <Shimmer className="dashboard-shimmer-chart" />
      </div>
    ))}
  </div>
);

const AnalysisShimmer = () => (
  <div className="dashboard-analysis-row">
    {[1, 2, 3].map((item) => (
      <div key={item} className="dashboard-analysis-card">
        <Shimmer className="dashboard-shimmer-line dashboard-shimmer-line--short" style={{ marginBottom: 16 }} />
        <Shimmer className="dashboard-shimmer-analysis" />
      </div>
    ))}
  </div>
);

const TemplatesShimmer = () => (
  <div className="dashboard-templates-grid">
    {[1, 2, 3].map((item) => (
      <div key={item} className="dashboard-shimmer-stat-card">
        <Shimmer className="dashboard-shimmer-line" />
        <Shimmer className="dashboard-shimmer-line dashboard-shimmer-line--short" style={{ marginTop: 12 }} />
      </div>
    ))}
  </div>
);

const StatCard = ({ title, value, subtitle, segments }) => (
  <div className="dashboard-stat-card">
    <div className="dashboard-stat-content">
      <div className="dashboard-stat-text">
        <p className="dashboard-stat-title">{title}</p>
        <h2 className="dashboard-stat-value">{value}</h2>
        <p className="dashboard-stat-trend">{subtitle}</p>
      </div>
      <div className="dashboard-stat-chart">
        <Doughnut
          data={{
            labels: ['', ''],
            datasets: [
              {
                data: segments,
                backgroundColor: ['rgba(255,255,255,0.92)', 'rgba(255,255,255,0.22)'],
                borderWidth: 0,
              },
            ],
          }}
          options={{
            maintainAspectRatio: false,
            cutout: '70%',
            plugins: { legend: { display: false }, tooltip: { enabled: false } },
          }}
        />
      </div>
    </div>
  </div>
);

const InsightCard = ({ icon, label, value, hint }) => (
  <div className="dashboard-insight-card">
    <span className="dashboard-insight-icon">
      <FontAwesomeIcon icon={icon} />
    </span>
    <div>
      <p className="dashboard-insight-label">{label}</p>
      <p className="dashboard-insight-value">{value}</p>
      {hint ? <p className="dashboard-insight-hint">{hint}</p> : null}
    </div>
  </div>
);

const ACCOUNT_FIELDS = [
  { key: 'quality_limit', label: 'Limit', icon: faGaugeHigh },
  { key: 'quality_status', label: 'Status', icon: faCircleCheck, status: true },
  { key: 'phone_number', label: 'Phone number', icon: faPhone, highlight: true },
  { key: 'waba_id', label: 'WABA ID', icon: faIdCard },
  { key: 'number_id', label: 'Number ID', icon: faHashtag },
];

const Dashboard = () => {
  const [myInfo, setMyInfo] = useState(null);
  const [balance, setBalance] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [templateCounts, setTemplateCounts] = useState([]);
  const [templateFilter, setTemplateFilter] = useState('Overall');
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [loadingBalance, setLoadingBalance] = useState(true);
  const [loadingAnalytics, setLoadingAnalytics] = useState(true);
  const [loadingCounts, setLoadingCounts] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    getMyInfo()
      .then((data) => setMyInfo(data))
      .catch(() => setError('Failed to load account info'))
      .finally(() => setLoadingInfo(false));

    getUserBalance()
      .then((data) => setBalance(data?.balance ?? 0))
      .catch(() => setBalance(0))
      .finally(() => setLoadingBalance(false));

    getDashboardAnalytics()
      .then((data) => setAnalytics(normalizeAnalytics(data)))
      .catch(() => setAnalytics(null))
      .finally(() => setLoadingAnalytics(false));
  }, []);

  useEffect(() => {
    fetchTemplateCounts()
      .then((data) => setTemplateCounts(Array.isArray(data) ? data : []))
      .catch(() => setTemplateCounts([]))
      .finally(() => setLoadingCounts(false));
  }, []);

  const userInfo = myInfo?.userInfo || {};
  const visibleAccountFields = useMemo(
    () => ACCOUNT_FIELDS.filter((field) => hasValue(userInfo[field.key])),
    [userInfo]
  );

  const todayStats = analytics?.periods?.today;
  const sums = useMemo(
    () => ({
      total: pickNum(todayStats, 'total'),
      read: pickNum(todayStats, 'read'),
      sent: pickNum(todayStats, 'sent'),
      delivered: pickNum(todayStats, 'delivered'),
      failed: pickNum(todayStats, 'failed'),
      pending: pickNum(todayStats, 'pending'),
      delivery_failed: pickNum(todayStats, 'delivery_failed'),
    }),
    [todayStats]
  );

  const failures = sums.failed + sums.delivery_failed;
  const pipeline = sums.sent + sums.pending;

  const statCards = useMemo(() => {
    if (!analytics) return [];

    const cards = [
      {
        key: 'balance',
        title: 'Balance',
        value: formatBalance(balance),
        subtitle: 'Account balance',
        segments: doughnutSafe(Number(balance) || 0, 1),
      },
      {
        key: 'total',
        title: "Today's messages",
        value: formatInt(sums.total),
        subtitle: deliveryRate(todayStats),
        segments: doughnutSafe(sums.delivered, Math.max(0, sums.total - sums.delivered)),
      },
      {
        key: 'delivered',
        title: 'Delivered',
        value: formatInt(sums.delivered),
        subtitle: 'Delivered today',
        segments: doughnutSafe(sums.delivered, Math.max(0, sums.total - sums.delivered)),
      },
      {
        key: 'read',
        title: 'Read',
        value: formatInt(sums.read),
        subtitle: 'Read today',
        segments: doughnutSafe(sums.read, Math.max(0, sums.total - sums.read)),
      },
      {
        key: 'pipeline',
        title: 'Sent + pending',
        value: formatInt(pipeline),
        subtitle: 'In flight today',
        segments: doughnutSafe(pipeline, Math.max(0, sums.total - pipeline)),
      },
      {
        key: 'failures',
        title: 'Failures',
        value: formatInt(failures),
        subtitle: 'Failed + delivery failed',
        segments: doughnutSafe(failures, Math.max(0, sums.total - failures)),
      },
      {
        key: 'yesterday',
        title: 'Yesterday',
        value: formatInt(pickNum(analytics.periods.yesterday, 'total')),
        subtitle: deliveryRate(analytics.periods.yesterday),
        segments: doughnutSafe(
          pickNum(analytics.periods.yesterday, 'delivered'),
          Math.max(0, pickNum(analytics.periods.yesterday, 'total') - pickNum(analytics.periods.yesterday, 'delivered'))
        ),
      },
      {
        key: 'last_7_days',
        title: 'Last 7 days',
        value: formatInt(pickNum(analytics.periods.last_7_days, 'total')),
        subtitle: deliveryRate(analytics.periods.last_7_days),
        segments: doughnutSafe(
          pickNum(analytics.periods.last_7_days, 'delivered'),
          Math.max(0, pickNum(analytics.periods.last_7_days, 'total') - pickNum(analytics.periods.last_7_days, 'delivered'))
        ),
      },
      {
        key: 'this_month',
        title: 'This month',
        value: formatInt(pickNum(analytics.periods.this_month, 'total')),
        subtitle: deliveryRate(analytics.periods.this_month),
        segments: doughnutSafe(
          pickNum(analytics.periods.this_month, 'delivered'),
          Math.max(0, pickNum(analytics.periods.this_month, 'total') - pickNum(analytics.periods.this_month, 'delivered'))
        ),
      },
      {
        key: 'incoming-today',
        title: 'Incoming today',
        value: formatInt(analytics.incoming_messages.today),
        subtitle: `${formatInt(analytics.incoming_messages.last_7_days)} in last 7 days`,
        segments: doughnutSafe(
          analytics.incoming_messages.today,
          Math.max(0, analytics.incoming_messages.last_7_days - analytics.incoming_messages.today)
        ),
      },
    ];

    return cards;
  }, [analytics, balance, failures, pipeline, sums, todayStats]);

  const statusRows = useMemo(
    () => [
      { label: 'Read', v: sums.read },
      { label: 'Sent', v: sums.sent },
      { label: 'Delivered', v: sums.delivered },
      { label: 'Failed', v: sums.failed },
      { label: 'Pending', v: sums.pending },
      { label: 'Del. failed', v: sums.delivery_failed },
    ],
    [sums]
  );

  const statusDoughnutData = useMemo(() => {
    if (!analytics) return null;
    const active = statusRows.filter((row) => row.v > 0);
    if (!active.length) {
      return {
        labels: ['No activity'],
        datasets: [{ data: [1], backgroundColor: [CHART_MUTED], borderWidth: 0 }],
      };
    }
    return {
      labels: statusRows.map((row) => row.label),
      datasets: [
        {
          data: statusRows.map((row) => row.v),
          backgroundColor: statusRows.map((row) => STATUS_COLORS[row.label]),
          borderWidth: 0,
        },
      ],
    };
  }, [analytics, statusRows]);

  const statusBarData = useMemo(() => {
    if (!analytics) return null;
    return {
      labels: statusRows.map((row) => row.label),
      datasets: [
        {
          label: 'Count',
          data: statusRows.map((row) => row.v),
          backgroundColor: statusRows.map((row) => STATUS_COLORS[row.label]),
          borderRadius: 6,
          borderWidth: 0,
        },
      ],
    };
  }, [analytics, statusRows]);

  const trendChartData = useMemo(() => {
    if (!analytics) return null;
    const trend = analytics.daily_trend || [];
    return {
      labels: trend.map((point) => formatShortDate(point.date)),
      datasets: [
        {
          label: 'Total',
          data: trend.map((point) => point.total),
          borderColor: ACCENT,
          backgroundColor: 'rgba(0, 168, 132, 0.15)',
          fill: true,
          tension: 0.35,
          borderWidth: 2,
        },
        {
          label: 'Delivered',
          data: trend.map((point) => point.delivered),
          borderColor: '#38bdf8',
          backgroundColor: 'transparent',
          tension: 0.35,
          borderWidth: 2,
        },
      ],
    };
  }, [analytics]);

  const barOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx) => formatInt(ctx.raw),
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { color: '#64748b', font: { size: 11 } },
        },
        y: {
          beginAtZero: true,
          grid: { color: CHART_MUTED },
          ticks: { color: '#64748b', maxTicksLimit: 8 },
        },
      },
    }),
    []
  );

  const lineOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: { color: '#64748b', boxWidth: 10, padding: 12, font: { size: 11 } },
        },
        tooltip: {
          callbacks: {
            label: (ctx) => ` ${ctx.dataset.label}: ${formatInt(ctx.raw)}`,
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { color: '#64748b', font: { size: 11 } },
        },
        y: {
          beginAtZero: true,
          grid: { color: CHART_MUTED },
          ticks: { color: '#64748b', maxTicksLimit: 6 },
        },
      },
    }),
    []
  );

  const doughnutOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: { color: '#64748b', boxWidth: 10, padding: 12, font: { size: 11 } },
        },
        tooltip: {
          callbacks: {
            label: (ctx) => ` ${ctx.label}: ${formatInt(ctx.raw)}`,
          },
        },
      },
    }),
    []
  );

  const filteredTemplateCounts = useMemo(() => {
    if (templateFilter === 'Overall') return templateCounts;
    return templateCounts.filter((count) => count.time_range === templateFilter);
  }, [templateCounts, templateFilter]);

  const waStatusEntries = useMemo(() => {
    const byStatus = analytics?.whatsapp_templates?.by_status || {};
    const entries = Object.entries(byStatus);
    if (entries.length) return entries;
    return [['No templates', 0]];
  }, [analytics]);

  const analyticsReady = !loadingAnalytics && analytics;
  const showAnalyticsShimmer = loadingAnalytics || loadingBalance;

  return (
    <div className="dashboard-wrap">
      <div className="dashboard-info-bar">
        {loadingInfo ? (
          <>
            <div className="dashboard-info-header">
              <span className="dashboard-info-title">Account overview</span>
            </div>
            <AccountOverviewShimmer />
          </>
        ) : (
          <>
            <div className="dashboard-info-header">
              <span className="dashboard-info-title">Account overview</span>
            </div>
            {visibleAccountFields.length > 0 ? (
              <div className="dashboard-info-grid">
                {visibleAccountFields.map((field) => (
                  <div
                    key={field.key}
                    className={`dashboard-info-item${field.highlight ? ' dashboard-info-item--highlight' : ''}`}
                  >
                    <span className="dashboard-info-icon">
                      <FontAwesomeIcon icon={field.icon} />
                    </span>
                    <div className="dashboard-info-meta">
                      <span className="dashboard-info-label">{field.label}</span>
                      <span
                        className={`dashboard-info-value${field.status ? ' dashboard-info-value--status' : ''}`}
                      >
                        {userInfo[field.key]}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="dashboard-info-loading">
                {error || 'WhatsApp account details are not linked yet.'}
              </p>
            )}
          </>
        )}
        <div className="dashboard-info-support">
          <FontAwesomeIcon icon={faHeadset} className="dashboard-info-support-icon" />
          <span>Need help?</span>
          <a href={`tel:+91${SALES_PHONE}`} className="dashboard-info-support-link">
            +91 {SALES_PHONE.replace(/(\d{2})(?=\d)/g, '$1 ').trim()}
          </a>
        </div>
      </div>

      {showAnalyticsShimmer ? (
        <>
          <StatCardsShimmer />
          <ChartsShimmer />
          <AnalysisShimmer />
        </>
      ) : analyticsReady ? (
        <>
          <div className="dashboard-cards-row">
            {statCards.map((card) => (
              <StatCard
                key={card.key}
                title={card.title}
                value={card.value}
                subtitle={card.subtitle}
                segments={card.segments}
              />
            ))}
          </div>

          <div className="dashboard-charts-row">
            <div className="dashboard-chart-card dashboard-chart-line">
              <h3>7-day message trend</h3>
              <div className="dashboard-chart-inner">
                {trendChartData ? (
                  <Line data={trendChartData} options={lineOptions} />
                ) : (
                  <p className="dashboard-chart-empty">No trend data</p>
                )}
              </div>
            </div>
            <div className="dashboard-chart-card dashboard-chart-doughnut">
              <h3>Today&apos;s status mix</h3>
              <div className="dashboard-chart-inner">
                {statusDoughnutData ? (
                  <Doughnut data={statusDoughnutData} options={doughnutOptions} />
                ) : (
                  <p className="dashboard-chart-empty">No status data</p>
                )}
              </div>
            </div>
            <div className="dashboard-chart-card dashboard-chart-line">
              <h3>Today&apos;s breakdown</h3>
              <div className="dashboard-chart-inner">
                {statusBarData ? (
                  <Bar data={statusBarData} options={barOptions} />
                ) : (
                  <p className="dashboard-chart-empty">No breakdown data</p>
                )}
              </div>
            </div>
          </div>

          <div className="dashboard-analysis-row">
            <div className="dashboard-analysis-card">
              <h3>Payment activity</h3>
              <div className="dashboard-insight-grid">
                <InsightCard
                  icon={faArrowUp}
                  label="Credits this month"
                  value={formatMoney(analytics.payments.this_month.credit)}
                  hint={`Overall ${formatMoney(analytics.payments.overall.credit)}`}
                />
                <InsightCard
                  icon={faArrowDown}
                  label="Debits this month"
                  value={formatMoney(analytics.payments.this_month.debit)}
                  hint={`Overall ${formatMoney(analytics.payments.overall.debit)}`}
                />
                <InsightCard
                  icon={faRotateLeft}
                  label="Refunds this month"
                  value={formatMoney(analytics.payments.this_month.refund)}
                  hint={`Overall ${formatMoney(analytics.payments.overall.refund)}`}
                />
              </div>
            </div>

            <div className="dashboard-analysis-card">
              <h3>Campaign summary</h3>
              <div className="dashboard-insight-grid">
                <InsightCard
                  icon={faBullhorn}
                  label="Total campaigns"
                  value={formatInt(analytics.campaigns.total)}
                />
                <InsightCard
                  icon={faCalendarDays}
                  label="Created this month"
                  value={formatInt(analytics.campaigns.this_month)}
                />
                <InsightCard
                  icon={faInbox}
                  label="Incoming (7 days)"
                  value={formatInt(analytics.incoming_messages.last_7_days)}
                  hint={`${formatInt(analytics.incoming_messages.today)} today`}
                />
              </div>
              <div className="dashboard-recent-table-wrap">
              <table className="dashboard-recent-table">
                <thead>
                  <tr>
                    <th>Campaign</th>
                    <th>Audience</th>
                    <th>Status</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.campaigns.recent.length > 0 ? (
                    analytics.campaigns.recent.map((campaign) => (
                      <tr key={campaign.id}>
                        <td>{campaign.name || `Campaign #${campaign.id}`}</td>
                        <td>{formatInt(campaign.totalnumber ?? 0)}</td>
                        <td>{campaign.status || '—'}</td>
                        <td>{formatDateTime(campaign.created_at)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="dashboard-chart-empty">
                        No campaigns yet
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              </div>
            </div>

            <div className="dashboard-analysis-card">
              <h3>WhatsApp templates</h3>
              <p className="dashboard-analysis-total">
                {formatInt(analytics.whatsapp_templates.total)} templates in account
              </p>
              <div className="dashboard-status-chips">
                {waStatusEntries.map(([status, count]) => (
                  <span
                    key={status}
                    className={`dashboard-status-chip ${
                      status === 'APPROVED' ? 'approved' : status === 'PENDING' ? 'pending' : ''
                    }`}
                  >
                    {status}: {formatInt(count)}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </>
      ) : (
        <p className="dashboard-no-data">Failed to load dashboard analytics.</p>
      )}

      <div className="dashboard-grid-row dashboard-grid-row--single">
        <div className="dashboard-templates-card">
          <div className="dashboard-templates-header">
            <h3>Templates Data</h3>
            <div className="dashboard-templates-filters">
              {['Overall', 'Last 30 Days', 'Last 7 Days'].map((filter) => (
                <button
                  key={filter}
                  type="button"
                  className={templateFilter === filter ? 'dashboard-filter-btn active' : 'dashboard-filter-btn'}
                  onClick={() => setTemplateFilter(filter)}
                  disabled={loadingCounts}
                >
                  {filter}
                </button>
              ))}
            </div>
          </div>
          {loadingCounts ? (
            <TemplatesShimmer />
          ) : filteredTemplateCounts.length > 0 ? (
            <div className="dashboard-templates-grid">
              {filteredTemplateCounts.map((card, index) => (
                <div key={index} className="dashboard-template-card">
                  <div className="dashboard-template-card-content">
                    <div className="dashboard-template-left">
                      <h4>{card.wa_template_category}</h4>
                      <span
                        className={`dashboard-template-status ${
                          card.wa_template_status === 'APPROVED' ? 'approved' : 'pending'
                        }`}
                      >
                        {card.wa_template_status}
                      </span>
                    </div>
                    <div className="dashboard-template-right">
                      <span className="dashboard-template-count">{formatInt(card.count)}</span>
                      <span>Audience</span>
                    </div>
                  </div>
                  <div className="dashboard-template-footer">{card.time_range}</div>
                </div>
              ))}
            </div>
          ) : (
            <p className="dashboard-no-data">No template data for this filter.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
